import { client, authHeaders } from '../../_shared/services/openapiClient';
import type { paths } from '../../../types/schema';
import baseUrl from '../../../utils/baseUrl';

// Types derived from OpenAPI schema
export type SearchBettieQuery = paths['/api/v1/bettie/search']['get']['parameters']['query'];
export type SearchBettieResponse =
  paths['/api/v1/bettie/search']['get']['responses'][200]['content']['application/json'];

export type CreateChatRoomBody = NonNullable<
  paths['/api/v1/bettie/create_chat_room']['post']['requestBody']
>['content']['application/json'];
export type CreateChatRoomResponse =
  paths['/api/v1/bettie/create_chat_room']['post']['responses'][200]['content']['application/json'];

export type CreateMessageBody = NonNullable<
  paths['/api/v1/bettie/create_message']['post']['requestBody']
>['content']['application/json'];
export type CreateMessageResponse =
  paths['/api/v1/bettie/create_message']['post']['responses'][201]['content']['application/json'];

export type GetBettieChatRoomsCountResponse =
  paths['/api/v1/bettie/chat_rooms_count']['get']['responses'][200]['content']['application/json'];

export type ResendBettieChatMessageResponse =
  paths['/api/v1/bettie/chat_messages/{chat_room_message_id}/resend']['post']['responses'][201]['content']['application/json'];

export type GetBettieDailyCheckInStatusResponse = NonNullable<
  paths['/api/v1/bettie/daily_check_in_reward_status']['get']['responses'][200]['content']['application/json']
>['bettieTransactions'];

export type ClaimBettieDailyCheckInRewardResponse =
  paths['/api/v1/bettie/daily_check_in_reward']['post']['responses'][200]['content']['application/json'];

export type UpdateBettieMoodSelectionBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['requestBody']
>['content']['application/json'];
export type UpdateBettieMoodSelectionResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['responses'][200]['content']['application/json'];

export type GetBettieDailyRandomRewardsResponse =
  paths['/api/v1/bettie/daily_random_rewards']['post']['responses'][200]['content']['application/json'];

export type GetBettieInfoResponse =
  paths['/api/v1/bettie/{bettie_id}']['get']['responses'][200]['content']['application/json']['bettie'];

export type GetBettieChatRoomPreferencesResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/user_preference']['get']['responses'][200]['content']['application/json']['context'];

export type GetBettieProactiveMessagesResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/proactive_messages']['get']['responses'][200]['content']['application/json'];

export type ActOnBettieProactiveMessageBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/proactive_messages/{proactive_message_history_id}/actions']['post']['requestBody']
>['content']['application/json'];
export type ActOnBettieProactiveMessageResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/proactive_messages/{proactive_message_history_id}/actions']['post']['responses'][200]['content']['application/json'];

export type UpdateBettieChatRoomPreferencesBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['requestBody']
>['content']['application/json'];
export type UpdateBettieChatRoomPreferencesResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/update_context']['post']['responses'][200]['content']['application/json']['chatRoom'];

export type CreateBettieVoiceCloneResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/voices']['post']['responses'][201]['content']['application/json'];

export type CreateBettieVoiceTtsBody = NonNullable<
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/voice_tts']['post']['requestBody']
>['content']['application/json'];
export type CreateBettieVoiceTtsResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/voice_tts']['post']['responses'][201]['content']['application/json'];

export type CreateBettieVoiceMessageResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/voice_messages']['post']['responses'][201]['content']['application/json'];

export type CreateBettieVoiceTranscriptResponse =
  paths['/api/v1/bettie/chat_rooms/{chat_room_id}/voice_transcripts']['post']['responses'][201]['content']['application/json'];

const formDataAuthHeaders = () => {
  const headers = { ...authHeaders() };
  delete (headers as Record<string, string>)['Content-Type'];
  return headers;
};

const parseApiResponse = async <T>(response: Response, errorMessage: string): Promise<T> => {
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    if (data instanceof Object && 'error' in data) {
      throw new Error(String((data as { error?: unknown }).error));
    }
    throw new Error(errorMessage);
  }

  if (!data) {
    throw new Error(errorMessage);
  }

  return data as T;
};

export async function searchBettie(params: SearchBettieQuery): Promise<SearchBettieResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/search', {
    params: { query: params },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response from bettie search');
  return data;
}

export async function createBettieChatRoom(
  body: CreateChatRoomBody
): Promise<CreateChatRoomResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/create_chat_room', {
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating chat room');
  return data;
}

export async function createBettieMessage(body: CreateMessageBody): Promise<CreateMessageResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/create_message', {
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating message');
  return data;
}

export async function getBettieChatRoomsCount(): Promise<GetBettieChatRoomsCountResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/chat_rooms_count', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie chat rooms count');
  return data;
}

export async function resendBettieChatMessage(
  chatRoomMessageId: string
): Promise<ResendBettieChatMessageResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_messages/{chat_room_message_id}/resend',
    {
      params: { path: { chat_room_message_id: chatRoomMessageId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when resending bettie chat message');
  return data;
}

export async function deleteBettieChatMessage(chatRoomMessageId: string): Promise<void> {
  const { error } = await client.DELETE(
    '/api/v1/bettie/chat_messages/{chat_room_message_id}/delete',
    {
      params: { path: { chat_room_message_id: chatRoomMessageId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  return;
}

export async function getBettieDailyCheckInStatus(): Promise<GetBettieDailyCheckInStatusResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/daily_check_in_reward_status', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie daily check in reward status');
  return data.bettieTransactions;
}

export async function claimBettieDailyCheckInReward(): Promise<ClaimBettieDailyCheckInRewardResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/daily_check_in_reward', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when claiming bettie daily check in reward');
  return data;
}

export async function updateBettieMoodSelection(
  chatRoomId: string,
  body: UpdateBettieMoodSelectionBody
): Promise<UpdateBettieMoodSelectionResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/update_context',
    {
      params: { path: { chat_room_id: chatRoomId } },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when updating bettie mood selection');
  return data;
}

export async function getBettieDailyRandomRewards(): Promise<GetBettieDailyRandomRewardsResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/daily_random_rewards', {
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie daily random rewards');
  return data;
}

export async function getBettieInfo(bettieId: string): Promise<GetBettieInfoResponse> {
  const { data, error } = await client.GET('/api/v1/bettie/{bettie_id}', {
    params: { path: { bettie_id: bettieId } },
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie info');
  return data.bettie;
}

export async function getBettieChatRoomPreferences(
  chatRoomId: string
): Promise<GetBettieChatRoomPreferencesResponse> {
  const { data, error } = await client.GET(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/user_preference',
    {
      params: { path: { chat_room_id: chatRoomId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie chat room preferences');
  return data.context;
}

export async function getBettieProactiveMessages(
  chatRoomId: string
): Promise<GetBettieProactiveMessagesResponse> {
  const { data, error } = await client.GET(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/proactive_messages',
    {
      params: { path: { chat_room_id: chatRoomId } },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting bettie proactive messages');
  return data;
}

export async function actOnBettieProactiveMessage(
  chatRoomId: string,
  proactiveMessageHistoryId: number,
  body: ActOnBettieProactiveMessageBody
): Promise<ActOnBettieProactiveMessageResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/proactive_messages/{proactive_message_history_id}/actions',
    {
      params: {
        path: {
          chat_room_id: chatRoomId,
          proactive_message_history_id: proactiveMessageHistoryId
        }
      },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when acting on bettie proactive message');
  return data;
}

export async function updateBettieChatRoomPreferences(
  chatRoomId: string,
  body: UpdateBettieChatRoomPreferencesBody
): Promise<UpdateBettieChatRoomPreferencesResponse> {
  const { data, error } = await client.POST(
    '/api/v1/bettie/chat_rooms/{chat_room_id}/update_context',
    {
      params: { path: { chat_room_id: chatRoomId } },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when updating bettie chat room preferences');
  return data.chatRoom;
}

export async function createBettieVoiceClone(
  chatRoomId: string,
  audioFile: Blob,
  options?: {
    removeBackgroundNoise?: boolean;
    fileName?: string;
  }
): Promise<CreateBettieVoiceCloneResponse> {
  const formData = new FormData();
  formData.append(
    'voice[audio_file]',
    audioFile,
    options?.fileName ?? 'bettie-voice-reference.webm'
  );
  if (options?.removeBackgroundNoise !== undefined) {
    formData.append('voice[remove_background_noise]', String(options.removeBackgroundNoise));
  }

  const response = await fetch(`${baseUrl()}/api/v1/bettie/chat_rooms/${chatRoomId}/voices`, {
    method: 'POST',
    headers: formDataAuthHeaders(),
    body: formData
  });

  return parseApiResponse<CreateBettieVoiceCloneResponse>(
    response,
    'Empty response when creating bettie voice clone'
  );
}

export async function createBettieVoiceTts(
  chatRoomId: string,
  body: CreateBettieVoiceTtsBody
): Promise<CreateBettieVoiceTtsResponse> {
  const { data, error } = await client.POST('/api/v1/bettie/chat_rooms/{chat_room_id}/voice_tts', {
    params: { path: { chat_room_id: chatRoomId } },
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating bettie voice tts');
  return data;
}

export async function createBettieVoiceMessage(
  chatRoomId: string,
  audioFile: Blob,
  options?: {
    tonePreset?: string;
    llmProvider?: 'gemini' | 'deepseek';
    llmModel?: string;
    settings?: Record<string, unknown>;
    fileName?: string;
  }
): Promise<CreateBettieVoiceMessageResponse> {
  const formData = new FormData();
  formData.append(
    'voice_message[audio_file]',
    audioFile,
    options?.fileName ?? 'bettie-voice-message.webm'
  );
  if (options?.tonePreset) formData.append('voice_message[tone_preset]', options.tonePreset);
  if (options?.llmProvider) formData.append('voice_message[llm_provider]', options.llmProvider);
  if (options?.llmModel) formData.append('voice_message[llm_model]', options.llmModel);
  if (options?.settings)
    formData.append('voice_message[settings]', JSON.stringify(options.settings));

  const response = await fetch(
    `${baseUrl()}/api/v1/bettie/chat_rooms/${chatRoomId}/voice_messages`,
    {
      method: 'POST',
      headers: formDataAuthHeaders(),
      body: formData
    }
  );

  return parseApiResponse<CreateBettieVoiceMessageResponse>(
    response,
    'Empty response when creating bettie voice message'
  );
}

export async function createBettieVoiceTranscript(
  chatRoomId: string,
  audioFile: Blob,
  options?: {
    fileName?: string;
  }
): Promise<CreateBettieVoiceTranscriptResponse> {
  const formData = new FormData();
  formData.append(
    'voice_transcript[audio_file]',
    audioFile,
    options?.fileName ?? 'bettie-voice-transcript.webm'
  );

  const response = await fetch(
    `${baseUrl()}/api/v1/bettie/chat_rooms/${chatRoomId}/voice_transcripts`,
    {
      method: 'POST',
      headers: formDataAuthHeaders(),
      body: formData
    }
  );

  return parseApiResponse<CreateBettieVoiceTranscriptResponse>(
    response,
    'Empty response when creating bettie voice transcript'
  );
}
